import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/kfmApi";
import HighlightableText from "@/components/HighlightableText";
import { Loader2, ArrowRight, MessageCircle } from "lucide-react";

function TopBar() {
  return (
    <header className="sticky top-0 z-20 bg-[#07090E]/85 backdrop-blur-md border-b border-[#1E273D]">
      <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="shared-deeper-logo" className="flex items-center gap-2">
          <img src="/openword-logo.png" alt="" className="w-6 h-6" />
          <span className="text-lg text-white">OpenWord AI</span>
        </Link>
        <Link
          to="/login"
          data-testid="shared-deeper-signin"
          className="text-sm font-medium text-[#07090E] bg-[#00E5FF] hover:bg-[#00B8CC] rounded-full px-5 h-9 flex items-center transition-colors"
        >
          Read along
        </Link>
      </div>
    </header>
  );
}

function SignupCta({ testid }) {
  return (
    <section
      data-testid={testid}
      className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-6 sm:p-8 text-center space-y-4"
    >
      <div className="flex items-center justify-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
        <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Go deeper yourself
      </div>
      <p className="text-xl text-slate-200 leading-relaxed">
        This is a Go Deeper conversation on OpenWord AI — a Scripture-first virtual study
        companion. Ask your own questions and explore any passage, free to begin.
      </p>
      <Link
        to="/login"
        data-testid={`${testid}-btn`}
        className="inline-flex items-center gap-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-7 h-12 text-base font-medium transition-colors"
      >
        Start your own conversation <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
      </Link>
      <p className="text-xs text-slate-500">No cost — and OpenWord AI is a study tool; the Word itself remains the belief system.</p>
    </section>
  );
}

export default function SharedDeeper() {
  const { token } = useParams();
  const [data, setData] = useState(undefined);
  const pinged = useRef(false);

  useEffect(() => {
    api
      .get(`/deeper-share/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
    if (!pinged.current) {
      pinged.current = true;
      api.post(`/deeper-share/${token}/view`).catch(() => {});
    }
  }, [token]);

  if (data === undefined) {
    return (
      <div className="min-h-screen bg-[#07090E] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="min-h-screen bg-[#07090E]">
        <TopBar />
        <div className="max-w-lg mx-auto px-6 py-24 text-center space-y-6">
          <h1 className="text-3xl text-white">This conversation isn&apos;t available.</h1>
          <p className="text-slate-400">The link may have been removed by its author.</p>
          <SignupCta testid="shared-deeper-cta-error" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#07090E]">
      <TopBar />
      <article data-testid="shared-deeper" className="max-w-3xl mx-auto px-6 py-10 sm:py-16 space-y-8">
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
            <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Go Deeper conversation
          </div>
          <h1 data-testid="shared-deeper-title" className="text-3xl sm:text-4xl text-white leading-tight">
            {data.title}
          </h1>
          {data.shared_by && <p className="text-sm text-slate-500">Shared by {data.shared_by}</p>}
        </div>

        <div className="space-y-4">
          {(data.chat || []).map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex flex-col items-end" : "flex flex-col items-start"}>
              <div
                data-testid={`shared-deeper-msg-${m.role}`}
                className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#00E5FF] text-[#07090E]" : "bg-[#0E131F] border border-[#1E273D] text-slate-300"
                }`}
              >
                {m.content
                  ? <HighlightableText block={`deeper-${i}`} text={m.content} highlights={data.highlights} editable={false} />
                  : ""}
              </div>
            </div>
          ))}
        </div>

        <SignupCta testid="shared-deeper-cta" />
      </article>
    </div>
  );
}
